import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  ProgressBar,
  Row,
  Spinner,
  Stack,
  Tab,
  Table,
  Tabs,
} from 'react-bootstrap';
import api from '../../api/api';
import AppHeader from '../Common/AppHeader';
import { showToast } from '../../utils/toast';

// --- Governance Constants ---
const BLOOM_LEVELS = ['L1', 'L2', 'L3', 'L4', 'L5', 'L6'];
const SECTION_TYPES = [
  { value: 'mcq', label: 'MCQ' },
  { value: 'true_false', label: 'True / False' },
  { value: 'short_note', label: 'Short Note' },
  { value: 'short_answer', label: 'Short Answer' },
  { value: 'essay', label: 'Detailed Essay' },
  { value: 'case_study', label: 'Case Study' },
];
const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
];

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const clampNumber = (value, min = 0) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, n);
};

const sumValues = (obj) => Object.values(obj || {}).reduce((acc, v) => acc + (Number(v) || 0), 0);

const getUniqueTopics = (conceptPool, selectedModules) => {
  return Array.from(
    new Map(
      conceptPool
        .filter(c => selectedModules.includes(c.module))
        .map(c => [`${c.module}__${c.topic}`, { module: c.module, topic: c.topic }])
    ).values()
  );
};

const getGraphConcepts = (graphSnapshot) => {
  if (!graphSnapshot) return [];
  if (Array.isArray(graphSnapshot)) return graphSnapshot;
  if (Array.isArray(graphSnapshot.concepts)) return graphSnapshot.concepts;
  if (Array.isArray(graphSnapshot.nodes)) return graphSnapshot.nodes.filter((n) => n.concept_name || n.type === 'concept');
  return [];
};

const emptySection = (index = 1) => ({
  id: `section_${uid()}`,
  name: `Part ${String.fromCharCode(64 + index)}`,
  type: 'mcq',
  count: 10,
  marks: 1,
  bloom_dist: { L1: 40, L2: 40, L3: 20, L4: 0, L5: 0, L6: 0 },
  difficulty_mix: { easy: 40, medium: 40, hard: 20 },
  concept_strategy: 'auto',
  coverage_target: 'balanced',
  allow_assets: [],
  selected_modules: [],
  selected_topics: [],
  selected_concepts: [],
  instructions: '',
});

const StatCard = ({ label, value, note }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body>
      <div className="text-muted small text-uppercase fw-bold mb-2">{label}</div>
      <div className="display-6 fw-bold">{value}</div>
      {note ? <div className="small text-muted">{note}</div> : null}
    </Card.Body>
  </Card>
);

const FacultyPreviewScreen = ({ project, blueprint, open, onClose }) => {
  const totalMarks = useMemo(() => blueprint.sections.reduce((acc, s) => acc + (Number(s.count || 0) * Number(s.marks || 0)), 0), [blueprint.sections]);
  const totalQuestions = useMemo(() => blueprint.sections.reduce((acc, s) => acc + Number(s.count || 0), 0), [blueprint.sections]);

  return (
    <Modal show={open} onHide={onClose} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <div>
          <div className="text-uppercase text-muted small fw-bold">Faculty Preview</div>
          <h4 className="mb-0">Assessment Blueprint Review</h4>
        </div>
      </Modal.Header>
      <Modal.Body className="bg-light">
        <Row className="g-4 mb-4">
          <Col md={3}><StatCard label="Project" value={project?.name || 'Untitled'} note={`Mode: ${project?.curriculum_mode}`} /></Col>
          <Col md={3}><StatCard label="Total Questions" value={totalQuestions} note={`Across ${blueprint.sections.length} section(s)`} /></Col>
          <Col md={3}><StatCard label="Total Marks" value={totalMarks} note="Faculty-controlled" /></Col>
        </Row>
        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="bg-white fw-bold">Section-by-Section Preview</Card.Header>
          <Card.Body className="p-0">
            <Table responsive className="mb-0 align-middle">
              <thead className="table-light small text-uppercase">
                <tr><th>Section</th><th>Type</th><th>Qty</th><th>Marks</th><th>Bloom</th><th>Difficulty</th></tr>
              </thead>
              <tbody>
                {blueprint.sections.map((sec) => (
                  <tr key={sec.id}>
                    <td><div className="fw-bold">{sec.name}</div><div className="small text-muted">{sec.instructions || 'No notes.'}</div></td>
                    <td><Badge bg="dark">{sec.type}</Badge></td>
                    <td>{sec.count}</td>
                    <td>{sec.marks}</td>
                    <td>{BLOOM_LEVELS.filter(l => sec.bloom_dist[l] > 0).map(l => <Badge key={l} bg="primary" className="me-1">{l}: {sec.bloom_dist[l]}%</Badge>)}</td>
                    <td>{Object.entries(sec.difficulty_mix).map(([k,v]) => <Badge key={k} bg="secondary" className="me-1">{k}: {v}%</Badge>)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer><Button variant="outline-secondary" onClick={onClose}>Close</Button></Modal.Footer>
    </Modal>
  );
};

const AssessmentBlueprintPage = () => {
  const { projectId, slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [graphSnapshot, setGraphSnapshot] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('sections');

  const [blueprint, setBlueprint] = useState({
    sections: [
      { ...emptySection(1), name: 'Part A (Objective)', type: 'mcq', count: 10, marks: 1, bloom_dist: { L1: 60, L2: 40, L3: 0, L4: 0, L5: 0, L6: 0 } },
      { ...emptySection(2), name: 'Part B (Subjective)', type: 'short_note', count: 5, marks: 5, bloom_dist: { L1: 0, L2: 30, L3: 70, L4: 0, L5: 0, L6: 0 } },
    ],
    co_mapping: {},
    name: '',
    difficulty_level: 'balanced',
    rules: { concept_strategy: 'auto', concept_coverage_target: 'balanced', bloom_enforcement: true, latex_validation: true, explanation_required: true, citation_required: true, rubric_required: true },
  });

  useEffect(() => {
    const fetchInitData = async () => {
      if (!slug || !projectId) return; 
      try {
        const [projectRes, graphRes] = await Promise.all([
          api.get(`/tenant/${slug}/projects/${projectId}/full-profile`),
          api.get(`/tenant/${slug}/projects/${projectId}/graph-snapshot`)
        ]);
        const projectData = projectRes.data;
        setProject(projectData);
        setGraphSnapshot(graphRes?.data || null);
        const autoCO = {};
        (projectData.curriculum_draft || []).forEach((m, i) => { 
          autoCO[m.title] = projectData.taxonomy_labels?.[m.title] || `CO${i + 1}`; 
        });
        setBlueprint(p => ({ ...p, co_mapping: autoCO, program_level: projectData.program_level }));
      } catch (err) { console.error("Blueprint Init Error:", err); } finally { setLoading(false); }
    };
    fetchInitData();
  }, [projectId, slug]);

    const conceptPool = useMemo(() => {
    const concepts = [];
    const rawNodes = getGraphConcepts(graphSnapshot);

    rawNodes.forEach((c, idx) => {
      // 1. DYNAMIC MAPPING: Find the module title by matching the concept unit_id 
      // to the curriculum structure
      const parentModule = project?.curriculum_draft?.find(m => 
        m.children?.some(u => parseInt(u.unit_id) === parseInt(c.unit_id))
      );

      concepts.push({ 
        id: c.id ?? `graph_${idx}`, 
        concept: c.name, 
        // Use the matched module title or fallback to the project name
        module: parentModule?.title || project?.name || "Unmapped", 
        unit_id: c.unit_id,
        topic: c.name, 
        isCore: Boolean(c.is_core), 
        bloom: c.bloom,
        difficulty: c.difficulty
      });
    });

    // 2. FALLBACK LOGIC: Handles Manual Mode projects (Project 73) where 
    // topics are simple strings
    if (!concepts.length && project?.curriculum_draft) {
      project.curriculum_draft.forEach(m => {
        m.children?.forEach(u => {
          u.topics?.forEach(t => {
            // Robust check for string-based topics vs object-based topics
            const topicTitle = typeof t === 'string' ? t : (t.title || t.topic_title);
            
            concepts.push({ 
              id: `fallback_${u.unit_id}_${topicTitle}`, 
              concept: topicTitle, 
              module: m.title, 
              unit_id: u.unit_id,
              topic: topicTitle, 
              isCore: false 
            });
          });
        });
      });
    }
    return concepts;
  }, [graphSnapshot, project]);

  const conceptStats = useMemo(() => {
    const totalConcepts = conceptPool.length;
    const readyConcepts = conceptPool.filter((c) => {
      const hasSource = project?.curriculum_mode === 'manual' || !!c.id;
      return hasSource;
    }).length;
    return { totalConcepts, readyConcepts, readinessScore: totalConcepts ? Math.round((readyConcepts / totalConcepts) * 100) : 0 };
  }, [conceptPool, project]);

  const validation = useMemo(() => {
    const errors = [];
    blueprint.sections.forEach((sec) => {
      if (sumValues(sec.bloom_dist) !== 100) errors.push(`${sec.name}: Bloom must total 100%.`);
      if (sumValues(sec.difficulty_mix) !== 100) errors.push(`${sec.name}: Difficulty must total 100%.`);
    });
    return { valid: errors.length === 0, errors };
  }, [blueprint]);

  const updateSection = (id, patch) => setBlueprint(p => ({ ...p, sections: p.sections.map(s => s.id === id ? { ...s, ...patch } : s) }));
  
  const updateSectionNested = (id, field, key, value) => setBlueprint(p => ({
    ...p, sections: p.sections.map(s => s.id === id ? { ...s, [field]: { ...s[field], [key]: clampNumber(value) } } : s)
  }));

  const handleSaveBlueprint = async () => {
    if (!validation.valid) { setActiveTab('governance'); return; }
    setSaving(true);
    
    try {
      // 1. Extract the Module/Unit Title from the first section's selection
      const selectedModuleName = blueprint.sections[0]?.selected_modules?.[0];
      
      // 2. Find the corresponding unit_id from the project curriculum draft
      const moduleData = project.curriculum_draft.find(m => m.title === selectedModuleName);
      
      // Fallback to unit_id 101 or 0 if no specific module is found to prevent 422 errors 
      const finalUnitId = moduleData?.unit_id || 101; 

      await api.post(`/tenant/${slug}/projects/${projectId}/blueprints`, {
        name: blueprint.name, 
        unit_id: parseInt(finalUnitId), // Required for Coverage QC
        mode: project.curriculum_mode, 
        template_json: { sections: blueprint.sections }, 
        outcomes_json: blueprint.co_mapping, 
        rules_json: { difficulty: blueprint.difficulty_level, ...blueprint.rules } 
      });
      
      navigate(`/tenant/${slug}/dashboard`);
    } catch (err) { 
      console.error("QC Mapping Error:", err.response?.data);
      showToast('Save Failed: Ensure a Module is selected for QC tracking.', 'error'); 
    } finally { 
      setSaving(false); 
    }
  };

  if (loading) return <div className="text-center py-5"><Spinner animation="border" aria-hidden="true" /></div>;

  return (
    <div className="bg-light min-vh-100 pb-5">
      <Container className="py-4">
        {/* --- GLOBAL BLUEPRINT CONFIGURATION (NEW) --- */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body className="p-4 bg-white rounded">
            <Row className="align-items-center">
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-uppercase text-primary">
                    Assessment Blueprint Name
                  </Form.Label>
                  <Form.Control 
                    size="lg"
                    type="text"
                    placeholder="e.g., Mid-Term Assessment - Biostatistics"
                    className="border-primary fw-bold"
                    value={blueprint.name} // Assuming 'name' is in your blueprint state
                    onChange={(e) => setBlueprint(p => ({ ...p, name: e.target.value }))}
                  />
                  <Form.Text className="text-muted">
                    Give this blueprint a clear name to identify it in the selection modal and generation history.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4} className="text-end border-start">
                <div className="text-muted small fw-bold">TOTAL ASSESSMENT MARKS</div>
                <div className="display-6 fw-bold text-primary">
                  {blueprint.sections.reduce((acc, sec) => acc + (Number(sec.count) * Number(sec.marks)), 0)}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
             <h2 className="fw-bold mb-0">Blueprint Builder: {project?.name}</h2>
             <p className="text-muted small">Design the structure for Project {projectId}</p>
          </div>
          <Stack direction="horizontal" gap={2}>
            <Button variant="secondary" onClick={() => navigate(`/tenant/${slug}/dashboard`)}>Back to Dashboard</Button>
            <Button variant="outline-dark" onClick={() => setShowPreview(true)}>Faculty Preview</Button>
            <Button variant="primary" className="fw-bold px-4" onClick={handleSaveBlueprint} disabled={saving}>
              {saving ? <Spinner size="sm" aria-hidden="true" /> : 'Finalize & Generate Bank'}
            </Button>
          </Stack>
        </div>

        <Tabs activeKey={activeTab} onSelect={k => setActiveTab(k)} className="mb-4 shadow-sm bg-white rounded">
          <Tab eventKey="sections" title="Sections & Structure">
            <Stack gap={4}>
              {blueprint.sections.map((sec, idx) => {
                const availableConcepts = conceptPool.filter((c) => {
                  if (!sec.selected_modules?.length) return false;
                  const moduleMatch = sec.selected_modules.includes(c.module);
                  const topicMatch = !sec.selected_topics?.length || sec.selected_topics.includes(c.topic);
                  return moduleMatch && topicMatch;
                });

                return (
                  <Card key={sec.id} className="border-0 shadow-sm p-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="fw-bold mb-0">Section {idx + 1}: {sec.name}</h5>
                      <Stack direction="horizontal" gap={2}>
                        <Button variant="outline-secondary" size="sm" onClick={() => setBlueprint(p => ({ ...p, sections: [...p.sections, { ...sec, id: `section_${uid()}`, name: `${sec.name} Copy` }] }))}>Duplicate</Button>
                        <Button variant="outline-danger" size="sm" onClick={() => setBlueprint(p => ({ ...p, sections: p.sections.filter(s => s.id !== sec.id) }))} disabled={blueprint.sections.length === 1}>Remove</Button>
                      </Stack>
                    </div>

                    <Row className="g-3 mb-4">

                      <Col md={4}><Form.Label className="small fw-bold">Name</Form.Label><Form.Control value={sec.name} onChange={e => updateSection(sec.id, { name: e.target.value })} /></Col>
                      <Col md={2}><Form.Label className="small fw-bold">Format</Form.Label>
                        <Form.Select value={sec.type} onChange={e => updateSection(sec.id, { type: e.target.value })}>
                          {SECTION_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </Form.Select>
                      </Col>
                      <Col md={2}><Form.Label className="small fw-bold">Qty</Form.Label><Form.Control type="number" value={sec.count} onChange={e => updateSection(sec.id, { count: clampNumber(e.target.value, 1) })} /></Col>
                      <Col md={2}><Form.Label className="small fw-bold">Marks</Form.Label><Form.Control type="number" value={sec.marks} onChange={e => updateSection(sec.id, { marks: clampNumber(e.target.value, 1) })} /></Col>
                      <Col md={2}><Form.Label className="small fw-bold">Total</Form.Label><Form.Control disabled value={Number(sec.count) * Number(sec.marks)} /></Col>
                    </Row>

                    <Row className="g-4 mb-4">
                      <Col lg={6}>
                        <Card className="bg-light border-0">
                          <Card.Header className="bg-transparent fw-bold small text-uppercase py-3">Bloom Distribution (%)</Card.Header>
                          <Card.Body>
                            <Row className="g-2">
                              {BLOOM_LEVELS.map(lvl => (
                                <Col md={4} key={lvl}>
                                  <Form.Label className="x-small text-muted">{lvl}</Form.Label>
                                  <InputGroup size="sm">
                                    <Form.Control type="number" defaultValue={sec.bloom_dist[lvl]} onBlur={(e) => updateSectionNested(sec.id, 'bloom_dist', lvl, e.target.value)} />
                                    <InputGroup.Text>%</InputGroup.Text>
                                  </InputGroup>
                                </Col>
                              ))}
                            </Row>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col lg={6}>
                        <Card className="bg-light border-0">
                          <Card.Header className="bg-transparent fw-bold small text-uppercase py-3">Difficulty Mix (%)</Card.Header>
                          <Card.Body>
                            <Row className="g-2">
                              {DIFFICULTY_OPTIONS.map(opt => (
                                <Col md={4} key={opt.value}>
                                  <Form.Label className="x-small text-muted">{opt.label}</Form.Label>
                                  <InputGroup size="sm">
                                    <Form.Control type="number" defaultValue={sec.difficulty_mix[opt.value]} onBlur={(e) => updateSectionNested(sec.id, 'difficulty_mix', opt.value, e.target.value)} />
                                    <InputGroup.Text>%</InputGroup.Text>
                                  </InputGroup>
                                </Col>
                              ))}
                            </Row>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    {/* --- TIERED SCOPE SELECTION (FULL WIDTH STACK) --- */}
                    <Stack gap={4} className="mt-4 pt-3 border-top">
                      <Form.Group>
                        <Form.Label className="small fw-bold text-uppercase text-primary">1. Module Scope</Form.Label>
                        <Form.Select 
                          multiple 
                          style={{ height: '120px', fontSize: '0.9rem' }} 
                          className="shadow-sm"
                          value={sec.selected_modules} 
                          onChange={e => updateSection(sec.id, { selected_modules: Array.from(e.target.selectedOptions).map(o => o.value) })}
                        >
                          {(project?.curriculum_draft || []).map(m => (
                            <option key={m.title} value={m.title} className="py-1 px-2 border-bottom">📂 {m.title}</option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">Select modules to filter the topics below.</Form.Text>
                      </Form.Group>

                      <Form.Group>
                        <Form.Label className="small fw-bold text-uppercase text-primary">2. Topic Scope</Form.Label>
                        <Form.Select 
                          multiple 
                          style={{ height: '160px', fontSize: '0.85rem' }} 
                          className="shadow-sm"
                          value={sec.selected_topics} 
                          onChange={e => updateSection(sec.id, { selected_topics: Array.from(e.target.selectedOptions).map(o => o.value) })}
                        >
                          {getUniqueTopics(conceptPool, sec.selected_modules).map(item => (
                            <option key={`${item.module}_${item.topic}`} value={item.topic} className="py-2 px-2 border-bottom text-wrap">
                              📍 {item.module} — {item.topic}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Form.Label className="small fw-bold text-uppercase text-primary mb-0">3. Concept Locks (Explicit Targeting)</Form.Label>
                          <Button 
                            size="sm" variant="outline-primary" style={{ fontSize: '0.7rem' }}
                            onClick={() => updateSection(sec.id, { selected_concepts: availableConcepts.filter(c => c.isCore).map(c => c.id) })}
                          >
                            Select All Core ⭐
                          </Button>
                        </div>
                        <Form.Select 
                          multiple 
                          style={{ height: '200px', fontSize: '0.85rem' }} 
                          className="shadow-sm"
                          value={sec.selected_concepts} 
                          onChange={e => updateSection(sec.id, { selected_concepts: Array.from(e.target.selectedOptions).map(o => o.value) })}
                        >
                          {availableConcepts.map((c) => (
                            <option key={c.id} value={c.id} className="py-2 px-2 border-bottom text-wrap">
                              {c.isCore ? '⭐ ' : '• '} {c.module} → {c.topic} → {c.concept} {c.bloom ? ` (${c.bloom})` : ''}
                            </option>
                          ))}
                        </Form.Select>
                        <div className="alert alert-info py-2 px-3 mt-2 small border-0">
                          <i className="bi bi-info-circle me-2"></i>
                          System prioritizes locked concepts. AI auto-maps across the entire scope if none selected.
                        </div>
                      </Form.Group>
                    </Stack>
                  </Card>
                );
              })}
              <Button variant="outline-primary" className="border-dashed py-3 fw-bold" onClick={() => setBlueprint(p => ({ ...p, sections: [...p.sections, emptySection(p.sections.length + 1)] }))}>+ Add New Section</Button>
            </Stack>
          </Tab>

          <Tab eventKey="outcomes" title="CO Alignment">
            <Card className="border-0 shadow-sm p-4">
               <h5 className="fw-bold mb-4">Course Outcome Mapping</h5>
               <Row className="g-3">
                 {Object.entries(blueprint.co_mapping).map(([mod, co]) => (
                   <Col md={6} key={mod}>
                     <div className="p-3 border rounded bg-white d-flex justify-content-between align-items-center">
                       <span className="fw-bold small">{mod}</span>
                       <Form.Control size="sm" style={{width: '100px'}} value={co} onChange={e => setBlueprint(p => ({ ...p, co_mapping: { ...p.co_mapping, [mod]: e.target.value } }))} />
                     </div>
                   </Col>
                 ))}
               </Row>
            </Card>
          </Tab>

          <Tab eventKey="governance" title="Governance & Rules">
            <Row className="g-4">
              <Col md={8}>
                <Card className="border-0 shadow-sm p-4 h-100">
                  <h5 className="fw-bold mb-4">Quality & Integrity Rules</h5>
                  <Stack gap={3}>
                    <Form.Check type="switch" label="Strict Bloom Enforcement" checked={blueprint.rules.bloom_enforcement} onChange={e => setBlueprint(p => ({ ...p, rules: { ...p.rules, bloom_enforcement: e.target.checked } }))} />
                    <Form.Check type="switch" label="Scientific Notation / LaTeX Validation" checked={blueprint.rules.latex_validation} onChange={e => setBlueprint(p => ({ ...p, rules: { ...p.rules, latex_validation: e.target.checked } }))} />
                    <Form.Check type="switch" label="Step-by-Step Marking Explanation" checked={blueprint.rules.explanation_required} onChange={e => setBlueprint(p => ({ ...p, rules: { ...p.rules, explanation_required: e.target.checked } }))} />
                    <Form.Check type="switch" label="Paragraph Citation for SME Review" checked={blueprint.rules.citation_required} onChange={e => setBlueprint(p => ({ ...p, rules: { ...p.rules, citation_required: e.target.checked } }))} />
                    <Form.Check type="switch" label="Generate Detailed Marking Rubrics" checked={blueprint.rules.rubric_required} onChange={e => setBlueprint(p => ({ ...p, rules: { ...p.rules, rubric_required: e.target.checked } }))} />
                  </Stack>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm p-4 border-top border-4 border-success text-center h-100">
                   <div className="text-muted small fw-bold">KNOWLEDGE GRAPH READY</div>
                   <div className="display-5 fw-bold my-2 text-success">{conceptStats.readinessScore}%</div>
                   <ProgressBar now={conceptStats.readinessScore} variant="success" className="mb-3" />
                   <p className="x-small text-muted">Percentage of concepts successfully anchored to source textbook.</p>
                </Card>
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </Container>
      <FacultyPreviewScreen open={showPreview} onClose={() => setShowPreview(false)} project={project} blueprint={blueprint} />
    </div>
  );
};

export default AssessmentBlueprintPage;
